import axios from "axios";
import { PriorityId, TicketId, TicketKey, TicketStatusId } from "./tickets";
import { ProjectId } from "./projects";
import type { ProjectRoleId } from "./projectConfigs";
import { parseDateOnly } from "@/utils";

export enum WorkQueueSource {
    Assigned = 0,
    Queued = 1,
}

export type WorkQueueItem = {
    id: TicketId;
    key: TicketKey;
    title: string;
    projectId: ProjectId;
    projectName: string;
    ticketStatusId: TicketStatusId;
    statusName: string;
    priorityId: PriorityId;
    priorityName: string;
    dueDate: Date | null;
    versionName: string | null;
    releaseDate: Date | null;
    source: WorkQueueSource;
    /** The role whose queue the ticket's status routes to; set for assigned tickets too. */
    queueRole: string | null;
};

/** A queue the user can draw from: a role they hold that some status routes to, and how they hold it. */
export type WorkQueueMembership = {
    projectId: ProjectId;
    projectName: string;
    roleId: ProjectRoleId;
    roleName: string;
    statusIds: TicketStatusId[];
    statusNames: string[];
    heldDirectly: boolean;
    teams: string[];
};

export type WorkQueue = {
    assigned: WorkQueueItem[];
    queued: WorkQueueItem[];
    queues: WorkQueueMembership[];
};

type WorkQueueItemResponse = Omit<WorkQueueItem, "dueDate" | "releaseDate"> & {
    dueDate: string | null;
    releaseDate: string | null;
};

export type WorkQueueApi = {
    get(): Promise<WorkQueue>;
    take(ticket: TicketId): Promise<void>;
};

function formatItem(item: WorkQueueItemResponse): WorkQueueItem {
    return {
        ...item,
        dueDate: item.dueDate ? new Date(item.dueDate) : null,
        releaseDate: item.releaseDate ? parseDateOnly(item.releaseDate) : null,
    };
}

export const workQueueApi: WorkQueueApi = {
    async get() {
        const { data } = await axios.get<{
            assigned: WorkQueueItemResponse[];
            queued: WorkQueueItemResponse[];
            queues: WorkQueueMembership[];
        }>("/api/v0/queue");
        return {
            assigned: data.assigned.map(formatItem),
            queued: data.queued.map(formatItem),
            queues: data.queues,
        };
    },
    async take(ticket) {
        await axios.post(`/api/v0/queue/${ticket}/take`);
    },
};
